// src/components/shared/NewsletterSignup.js
// Reusable newsletter capture widget.
// Props:
//   variant: 'section' (full homepage block) | 'footer' (compact inline)
//   source:  tracking tag stored with the subscriber row

import React, { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useApp } from '../../lib/AppContext';

const BENEFITS = [
  { icon: '🌿', text: 'First access to new drops' },
  { icon: '🎰', text: '+1 free wheel spin on signup' },
  { icon: '💎', text: 'Exclusive subscriber discounts' },
  { icon: '📦', text: 'Restock alerts before anyone else' },
];

export default function NewsletterSignup({ variant = 'section', source = 'homepage' }) {
  const { user, profile } = useApp();

  const [email, setEmail]   = useState(user?.email || '');
  const [name,  setName]    = useState(profile?.full_name || '');
  const [gdpr,  setGdpr]    = useState(false);
  const [state, setState]   = useState('idle'); // 'idle' | 'loading' | 'success' | 'error'
  const [errorMsg, setErr]  = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) {
      setErr('Please enter a valid email address.'); setState('error'); return;
    }
    if (!gdpr) {
      setErr('Please accept the privacy policy to subscribe.'); setState('error'); return;
    }
    setState('loading');

    const now = new Date().toISOString();
    const { error } = await supabase.from('newsletter_subscribers').upsert({
      email:              email.trim().toLowerCase(),
      name:               name.trim() || null,
      active:             true,
      language:           'en',
      country:            'DE',
      source,
      gdpr_consent:       true,
      gdpr_consent_at:    now,
      subscribed_at:      now,
    }, { onConflict: 'email', ignoreDuplicates: false });

    if (error) {
      setErr('Something went wrong. Please try again.');
      setState('error');
      return;
    }

    // Grant +1 spin if user is logged in and hasn't claimed newsletter spin yet
    if (user?.id) {
      const { data: prof } = await supabase
        .from('profiles')
        .select('spin_balance, newsletter_spin_claimed')
        .eq('id', user.id)
        .maybeSingle();
      if (prof && !prof.newsletter_spin_claimed) {
        await supabase.from('profiles').update({
          spin_balance:            (prof.spin_balance || 0) + 1,
          newsletter_spin_claimed: true,
        }).eq('id', user.id);
      }
    }

    setState('success');
  };

  // ── Section variant (homepage) ────────────────────────────────────────────
  if (variant === 'section') {
    if (state === 'success') {
      return (
        <div style={{
          background: 'linear-gradient(135deg, rgba(76,202,116,.08) 0%, rgba(26,122,62,.04) 100%)',
          border: '1px solid rgba(76,202,116,.2)',
          borderRadius: 24, padding: '48px 32px', textAlign: 'center',
          maxWidth: 600, margin: '0 auto',
        }}>
          <div style={{ fontSize: '3.5rem', marginBottom: 16 }}>🎉</div>
          <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: '2rem', letterSpacing: '.04em', color: '#4cca74', marginBottom: 8 }}>
            You're in!
          </div>
          <p style={{ color: '#888', fontSize: '.92rem', lineHeight: 1.8, maxWidth: 380, margin: '0 auto 20px' }}>
            Welcome to the CosmiqHerbz inner circle. Check your inbox for a confirmation.
            {user?.id && ' Your free wheel spin has been added to your account.'}
          </p>
          {user?.id && (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: 'rgba(76,202,116,.1)', border: '1px solid rgba(76,202,116,.25)',
              borderRadius: 20, padding: '8px 18px',
            }}>
              <span>🎰</span>
              <span style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '.82rem', fontWeight: 700, color: '#4cca74' }}>
                +1 spin added to your account
              </span>
            </div>
          )}
        </div>
      );
    }

    return (
      <section style={{ padding: '64px 24px', background: 'var(--bg2, #111)' }}>
        <div style={{ maxWidth: 680, margin: '0 auto' }}>

          {/* Eyebrow + heading */}
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <span style={{
              fontFamily: "'Barlow Condensed',sans-serif",
              fontSize: '.7rem', fontWeight: 700, letterSpacing: '.22em',
              textTransform: 'uppercase', color: '#4cca74', display: 'block', marginBottom: 10,
            }}>
              Inner Circle
            </span>
            <h2 style={{
              fontFamily: "'Bebas Neue',sans-serif",
              fontSize: 'clamp(2rem,5vw,2.8rem)', letterSpacing: '.04em',
              marginBottom: 12, lineHeight: 1,
            }}>
              Stay ahead of the drop
            </h2>
            <p style={{ color: '#666', fontSize: '.9rem', lineHeight: 1.8, maxWidth: 460, margin: '0 auto' }}>
              Join thousands of collectors who never miss a restock, exclusive deal, or limited drop.
            </p>
          </div>

          {/* Benefit pills */}
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 10, marginBottom: 32,
          }}>
            {BENEFITS.map(b => (
              <div key={b.text} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                background: 'rgba(255,255,255,.03)',
                border: '1px solid rgba(255,255,255,.07)',
                borderRadius: 10, padding: '10px 14px',
              }}>
                <span style={{ fontSize: '1.1rem', flexShrink: 0 }}>{b.icon}</span>
                <span style={{ fontSize: '.78rem', color: '#888', lineHeight: 1.4 }}>{b.text}</span>
              </div>
            ))}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} style={{ background: '#0d0d0d', border: '1px solid rgba(255,255,255,.07)', borderRadius: 18, padding: '28px 24px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 12 }}>
              <div>
                <div className="input-label">First Name</div>
                <input
                  className="input-field"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Jane"
                  autoComplete="given-name"
                />
              </div>
              <div>
                <div className="input-label">Email Address *</div>
                <input
                  className="input-field"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  autoComplete="email"
                  required
                />
              </div>
            </div>

            {/* GDPR */}
            <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer', marginBottom: 16 }}>
              <input
                type="checkbox"
                checked={gdpr}
                onChange={e => setGdpr(e.target.checked)}
                style={{ marginTop: 3, accentColor: '#4cca74', width: 15, height: 15, flexShrink: 0 }}
              />
              <span style={{ fontSize: '.75rem', color: '#666', lineHeight: 1.7 }}>
                I agree to receive marketing emails from CosmiqHerbz and accept the{' '}
                <a href="/legal/privacy" style={{ color: '#4cca74', textDecoration: 'none' }}>Privacy Policy</a>.
                {' '}Unsubscribe anytime. EU GDPR compliant.
              </span>
            </label>

            {/* Error */}
            {state === 'error' && (
              <div style={{ fontSize: '.78rem', color: '#e05050', marginBottom: 12, background: 'rgba(224,80,80,.08)', border: '1px solid rgba(224,80,80,.2)', borderRadius: 8, padding: '8px 12px' }}>
                {errorMsg}
              </div>
            )}

            <button
              type="submit"
              className="btn-primary"
              disabled={state === 'loading'}
              style={{ width: '100%', fontSize: '.95rem', padding: '14px' }}
            >
              {state === 'loading'
                ? <div className="spinner" style={{ width: 18, height: 18, borderWidth: 2, margin: '0 auto' }} />
                : '✉️ Subscribe & Get Free Spin'}
            </button>

            <p style={{ textAlign: 'center', fontSize: '.68rem', color: '#333', marginTop: 10 }}>
              No spam. Unsubscribe anytime. 18+ only.
            </p>
          </form>
        </div>
      </section>
    );
  }

  // ── Footer variant (compact inline) ──────────────────────────────────────
  if (state === 'success') {
    return (
      <div style={{ marginTop: 16 }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          background: 'rgba(76,202,116,.1)', border: '1px solid rgba(76,202,116,.2)',
          borderRadius: 10, padding: '10px 14px',
        }}>
          <span>✓</span>
          <span style={{ fontSize: '.78rem', color: '#4cca74' }}>
            Subscribed! {user?.id ? '+1 spin added.' : 'Check your inbox.'}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ fontSize: '.72rem', color: '#555', fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 8 }}>
        Newsletter
      </div>
      <p style={{ fontSize: '.78rem', color: '#555', marginBottom: 10, lineHeight: 1.6 }}>
        New drops, restocks & collector deals — straight to your inbox.
      </p>
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
          <input
            className="input-field"
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="your@email.com"
            autoComplete="email"
            style={{ flex: 1, fontSize: '.82rem', padding: '9px 14px' }}
            required
          />
          <button
            type="submit"
            disabled={state === 'loading'}
            style={{
              background: '#4cca74', border: 'none', borderRadius: 10,
              padding: '0 16px', color: '#000', fontFamily: "'Barlow Condensed',sans-serif",
              fontSize: '.82rem', fontWeight: 800, letterSpacing: '.06em',
              cursor: 'pointer', whiteSpace: 'nowrap', minHeight: 44,
              flexShrink: 0,
            }}
          >
            {state === 'loading' ? '…' : 'Join'}
          </button>
        </div>

        {/* Inline GDPR */}
        <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={gdpr}
            onChange={e => setGdpr(e.target.checked)}
            style={{ marginTop: 2, accentColor: '#4cca74', width: 13, height: 13, flexShrink: 0 }}
          />
          <span style={{ fontSize: '.68rem', color: '#444', lineHeight: 1.6 }}>
            I accept the{' '}
            <a href="/legal/privacy" style={{ color: '#555' }}>Privacy Policy</a> and agree to receive emails.
          </span>
        </label>

        {state === 'error' && (
          <div style={{ fontSize: '.72rem', color: '#e05050', marginTop: 6 }}>{errorMsg}</div>
        )}
      </form>
    </div>
  );
}
